/**
 * parts/booth.js — the stall booth and its structural furniture.
 *
 * The booth is the piece that sells the midway: twelve of them are most of what
 * the player ever sees. It is built as a real stall — corner posts with turned
 * finials, a striped awning sloping to a scalloped valance, a painted counter,
 * a lettered fascia and a bunting swag across the front.
 *
 * SPACE: origin at the centre of the front face, on the ground. +Z runs INTO
 * the booth, +X across the front, and the opening faces −Z.
 *
 * TIGHT CLEARANCE — `world/hub.js` hangs its ticket sign at y 2.04–2.66, local
 * z ≈ 0.12, and the player stands only 0.7 m out at z = −0.7. Nothing may
 * overhang past z = −0.45 or intrude on that sign's band, or the booth either
 * eats its own signage or swallows the third-person camera.
 *
 * @module assets3d/parts/booth
 */

import * as THREE from 'three';
import { ART, boothFamily, mix, shade } from '../palette.js';
import {
  stripes, scallopAlpha, woodGrain, wallPattern, buntingStrip, bulbBand, starburst,
  barberStripe, paint, showcardText,
} from '../textures.js';
import { geo, mat, mesh, slab, bulb, finial, createSign } from './core.js';

/** How far a shuttered booth desaturates toward warm grey. */
const MUTE = 0.55;
const MUTE_GREY = 0x9a8f86;

/**
 * The diagonal COMING SOON banner slung across a shuttered booth's opening.
 * @returns {THREE.CanvasTexture}
 */
function comingSoonBanner() {
  return paint('coming-soon-banner', 1024, 128, (ctx, w, h) => {
    ctx.fillStyle = `#${ART.plumDeep.toString(16)}`;
    ctx.fillRect(0, 0, w, h);
    // Hazard diagonals at the ends.
    ctx.save();
    ctx.fillStyle = `#${ART.gold.toString(16)}`;
    for (const x0 of [0, w - 150]) {
      for (let i = 0; i < 8; i++) {
        ctx.beginPath();
        ctx.moveTo(x0 + i * 22, 0);
        ctx.lineTo(x0 + i * 22 + 11, 0);
        ctx.lineTo(x0 + i * 22 - 22, h);
        ctx.lineTo(x0 + i * 22 - 33, h);
        ctx.closePath();
        ctx.fill();
      }
    }
    ctx.restore();
    showcardText(ctx, 'COMING SOON', w / 2, h / 2, {
      size: 78, fill: `#${ART.gold.toString(16)}`,
      outline: '#1a1016', highlight: 'rgba(255,255,255,0.28)',
    });
  });
}

/**
 * A carnival stall booth. Opens toward −Z.
 *
 * @param {{ name?: string, color?: string|number, width?: number, depth?: number,
 *           height?: number, comingSoon?: boolean }} [opts]
 * @returns {THREE.Group} with `userData.dimensions` and `userData.setComingSoon()`.
 */
export function createStallBooth(opts = {}) {
  const width = opts.width ?? 5;
  const depth = opts.depth ?? 6.5;
  const height = opts.height ?? 3.6;
  const fam = boothFamily(opts.color);

  const g = new THREE.Group();
  g.name = 'booth';

  // Meshes that lose their colour when the booth is shuttered.
  /** @type {Array<{ mesh: THREE.Mesh, vivid: THREE.Material, muted: THREE.Material }>} */
  const mutable = [];

  /**
   * A mesh whose colour is dulled when the stall is closed.
   * @param {THREE.BufferGeometry} geometry
   * @param {number} color
   * @param {object} [matOpts]
   * @param {object} [meshOpts]
   */
  const painted = (geometry, color, matOpts = {}, meshOpts = {}) => {
    const m = mesh(geometry, mat(color, matOpts), meshOpts);
    mutable.push({
      mesh: m,
      vivid: m.material,
      muted: mat(mix(color, MUTE_GREY, MUTE), matOpts),
    });
    return m;
  };

  // ── Shell ─────────────────────────────────────────────────────────────────
  // Deck boards, not a dark slab: a hub booth has no light of its own until a
  // stall adds one, and a dark floor turns the whole interior into a black hole.
  const floor = mesh(
    geo(`booth-floor-${width}-${depth}`, () => new THREE.BoxGeometry(width, 0.1, depth)),
    mat(ART.woodLight, {
      rough: 0.92,
      map: woodGrain(ART.woodLight, [1, Math.round(depth / 1.2)]),
    }),
    { cast: false },
  );
  floor.position.set(0, 0.05, depth / 2);
  g.add(floor);

  const wallMat = mat(ART.cream, {
    rough: 0.92,
    map: wallPattern(ART.cream, ART.creamShade, [Math.round(width / 1.1), Math.round(height / 1.1)]),
  });
  const backWall = mesh(
    geo(`booth-back-${width}-${height}`, () => new THREE.BoxGeometry(width, height, 0.18)),
    wallMat,
  );
  backWall.position.set(0, height / 2, depth);
  g.add(backWall);

  // A dado rail breaks the back wall's height and gives the interior a floor line.
  const rail = painted(slab(width, 0.11, 0.22, 0.02), fam.trim, { rough: 0.8 });
  rail.position.set(0, 1.55, depth - 0.10);
  g.add(rail);

  for (const s of [-1, 1]) {
    const side = mesh(
      geo(`booth-side-${height}-${depth}`, () => new THREE.BoxGeometry(0.16, height, depth)),
      wallMat,
    );
    side.position.set((width / 2) * s, height / 2, depth / 2);
    g.add(side);
  }

  // ── Corner posts ──────────────────────────────────────────────────────────
  const postTop = height + 0.75;
  for (const s of [-1, 1]) {
    const post = painted(
      geo(`booth-post-${postTop}`, () => new THREE.CylinderGeometry(0.10, 0.115, postTop, 12)),
      fam.trim, { rough: 0.8, map: woodGrain(ART.woodLight, [1, Math.round(postTop / 1.5)]) },
    );
    post.position.set((width / 2) * s, postTop / 2, 0.02);
    g.add(post);

    // Turned collar and ball finial.
    const collar = painted(
      geo('booth-collar', () => new THREE.CylinderGeometry(0.135, 0.135, 0.08, 12)),
      fam.valance, { rough: 0.6 },
    );
    collar.position.set((width / 2) * s, postTop, 0.02);
    g.add(collar);

    const knob = finial(0.115, ART.gold);
    knob.position.set((width / 2) * s, postTop + 0.12, 0.02);
    g.add(knob);
  }

  // ── Counter ───────────────────────────────────────────────────────────────
  const counter = painted(slab(width, 0.92, 0.55, 0.02), fam.canopy, { rough: 0.85 });
  counter.position.set(0, 0.46, 0.33);
  counter.name = 'counter';
  g.add(counter);

  // Wainscot panels on the counter front, which is the surface a player is
  // closest to for the whole round.
  const panels = Math.max(3, Math.round(width / 0.85));
  for (let i = 0; i < panels; i++) {
    const pw = (width / panels) * 0.74;
    const p = painted(slab(pw, 0.46, 0.04, 0.012), fam.canopyB, { rough: 0.85 });
    p.position.set(-width / 2 + (width / panels) * (i + 0.5), 0.47, 0.045);
    g.add(p);
  }

  const trim = painted(slab(width + 0.03, 0.10, 0.58, 0.02), fam.valance, { rough: 0.7 });
  trim.position.set(0, 0.855, 0.33);
  g.add(trim);

  const counterTop = mesh(
    slab(width + 0.16, 0.10, 0.68, 0.025),
    mat(ART.wood, { rough: 0.7, map: woodGrain(ART.wood, [Math.round(width / 1.4), 1]) }),
  );
  counterTop.position.set(0, 0.965, 0.32);
  counterTop.name = 'counter-top';
  g.add(counterTop);

  // ── Pilasters framing the opening ─────────────────────────────────────────
  for (const s of [-1, 1]) {
    const pil = painted(slab(0.34, 2.05, 0.26, 0.02), fam.canopy, { rough: 0.82 });
    pil.position.set((width / 2 - 0.32) * s, 2.04, 0.11);
    g.add(pil);

    const capital = painted(slab(0.42, 0.12, 0.32, 0.02), fam.valance, { rough: 0.7 });
    capital.position.set((width / 2 - 0.32) * s, 3.12, 0.11);
    g.add(capital);
  }

  // ── Awning ────────────────────────────────────────────────────────────────
  // Slopes from high at the back to a low front lip. The overhang past the
  // front face is only 0.35 m — see the clearance note at the top of the file.
  const frontZ = -0.35, frontY = 3.15;
  const backZ = 1.70, backY = 3.85;
  const dz = backZ - frontZ, dy = backY - frontY;
  const awnLen = Math.hypot(dz, dy);
  const bandCount = Math.max(6, Math.round(width / 0.62)) * 2;

  const awning = painted(
    geo(`booth-awning-${width}-${awnLen.toFixed(3)}`,
      () => new THREE.BoxGeometry(width + 0.34, 0.07, awnLen)),
    fam.canopy,
    { rough: 0.78, map: stripes(fam.canopy, fam.canopyB, bandCount), side: THREE.DoubleSide },
  );
  awning.position.set(0, (frontY + backY) / 2, (frontZ + backZ) / 2);
  awning.rotation.x = -Math.atan2(dy, dz);
  awning.name = 'awning';
  g.add(awning);

  // A canvas roof over the rest of the booth. The striped awning only reaches
  // 1.7 m in, so without this the player standing at the counter looks up past
  // the targets straight into the sky — the in-stall screenshot had stars for
  // a ceiling. It deliberately does NOT cast a shadow: it needs to occlude the
  // sky, not black out the interior a stall has only one point light for.
  // Runs from the awning's back edge down onto the top of the back wall, so
  // there is no slot of sky left between the two.
  const roofBackZ = depth + 0.12;
  const roofBackY = height + 0.02;
  const rdz = roofBackZ - backZ, rdy = roofBackY - backY;
  const roofLen = Math.hypot(rdz, rdy);
  const roof = mesh(
    geo(`booth-roof-${width}-${roofLen.toFixed(3)}`,
      () => new THREE.BoxGeometry(width + 0.06, 0.07, roofLen)),
    mat(ART.creamWarm, { rough: 0.94, side: THREE.DoubleSide }),
    { cast: false },
  );
  roof.position.set(0, (backY + roofBackY) / 2, (backZ + roofBackZ) / 2);
  roof.rotation.x = -Math.atan2(rdy, rdz);
  roof.name = 'roof';
  g.add(roof);

  // Scalloped valance hanging off the front lip. This is the booth's signature
  // read — the awning itself is seen almost edge-on from the counter, so the
  // valance has to carry the stripes. Made deep enough to actually register.
  const valanceH = 0.62;
  const valance = painted(
    geo(`booth-valance-${width}-${valanceH}`,
      () => new THREE.PlaneGeometry(width + 0.34, valanceH)),
    fam.canopy,
    {
      rough: 0.8, side: THREE.DoubleSide,
      map: stripes(fam.canopy, fam.canopyB, bandCount),
      alphaMap: scallopAlpha(Math.max(8, Math.round(width * 2.4))),
    },
    { cast: false },
  );
  valance.position.set(0, frontY - valanceH / 2, frontZ - 0.02);
  valance.name = 'valance';
  g.add(valance);

  // ── Fascia and name board ─────────────────────────────────────────────────
  const fasciaY = 3.70;
  const fascia = painted(slab(width + 0.30, 0.80, 0.22, 0.02), fam.sign, { rough: 0.84 });
  fascia.position.set(0, fasciaY, 0);
  g.add(fascia);

  // A starburst behind the lettering, so the name reads off a decorated ground
  // rather than a flat plum slab.
  const burst = mesh(
    geo(`booth-burst-${width}`, () => new THREE.PlaneGeometry(width * 0.9, 0.62)),
    mat(0xffffff, {
      flat: true, map: starburst(fam.valance, fam.sign, 18),
      // Kept faint: at 0.55 the rays competed with the lettering and the name
      // stopped being the first thing you read.
      transparent: true, opacity: 0.28,
    }),
    { cast: false, receive: false },
  );
  burst.position.set(0, fasciaY, -0.113);
  burst.rotation.y = Math.PI;
  g.add(burst);

  for (const dy2 of [0.40, -0.40]) {
    const band = mesh(
      geo(`booth-band-${width}`, () => new THREE.PlaneGeometry(width + 0.30, 0.10)),
      mat(0xffffff, { flat: true, map: bulbBand(Math.max(8, Math.round(width * 3.2))) }),
      { cast: false, receive: false },
    );
    band.position.set(0, fasciaY + dy2, -0.114);
    band.rotation.y = Math.PI;
    g.add(band);
  }

  // Real bulbs along the fascia underside — the texture band alone does not
  // give the booth any actual pools of light in the frame.
  const lampCount = Math.max(4, Math.round(width / 1.1));
  for (let i = 0; i < lampCount; i++) {
    const b = bulb(0.062, ART.bulbHot);
    b.position.set(
      -width / 2 + (width / (lampCount - 1)) * i,
      fasciaY - 0.46,
      -0.10,
    );
    g.add(b);
  }

  // ── Hanging counter lamps ─────────────────────────────────────────────────
  for (const s of [-1, 1]) {
    const bracket = mesh(
      geo('booth-bracket', () => new THREE.CylinderGeometry(0.018, 0.018, 0.40, 6)),
      mat(ART.iron, { rough: 0.5, metal: 0.6 }),
    );
    bracket.position.set(width * 0.26 * s, 3.18, 0.30);
    g.add(bracket);

    const shade2 = mesh(
      geo('booth-shade', () => new THREE.ConeGeometry(0.15, 0.14, 12, 1, true)),
      mat(ART.red, { rough: 0.6, side: THREE.DoubleSide }),
    );
    shade2.position.set(width * 0.26 * s, 2.96, 0.30);
    g.add(shade2);

    const lamp = bulb(0.075, ART.bulbHot);
    lamp.position.set(width * 0.26 * s, 2.90, 0.30);
    g.add(lamp);
  }

  // ── Bunting across the front ──────────────────────────────────────────────
  const bunting = buntingStrip([fam.canopy, ART.cream, fam.valance, ART.teal]);
  const swag = mesh(
    geo(`bunting-${width}`, () => {
      // A shallow catenary between the two corner posts.
      const g2 = new THREE.PlaneGeometry(width + 0.2, 0.30, 24, 1);
      const pos = g2.attributes.position;
      for (let i = 0; i < pos.count; i++) {
        const t = (pos.getX(i) / (width + 0.2)) + 0.5;
        pos.setY(i, pos.getY(i) - Math.sin(t * Math.PI) * 0.20);
      }
      g2.computeVertexNormals();
      return g2;
    }),
    mat(0xffffff, {
      flat: true, map: bunting.map, alphaMap: bunting.alpha,
      side: THREE.DoubleSide, transparent: true,
    }),
    { cast: false, receive: false },
  );
  // Strung between the post finials, ABOVE the fascia. Hung at the awning lip
  // it sat directly over the scalloped valance and the two patterns cancelled
  // each other out — the front of the booth read as visual noise.
  swag.position.set(0, postTop + 0.10, -0.30);
  swag.name = 'bunting';
  g.add(swag);

  // ── Name board ────────────────────────────────────────────────────────────
  const label = createSign(opts.comingSoon ? 'COMING SOON' : (opts.name || ''), {
    w: width * 0.84, h: 0.58,
    bg: 'transparent',
    color: `#${ART.cream.toString(16)}`,
    border: null,
    doubleSided: true,
  });
  label.position.set(0, fasciaY, -0.118);
  label.rotation.y = Math.PI;
  label.name = 'booth-sign';
  g.add(label);

  // ── Shuttered state ───────────────────────────────────────────────────────
  const bannerPivot = new THREE.Group();
  bannerPivot.rotation.y = Math.PI;
  bannerPivot.position.set(0, 1.70, 0.55);
  g.add(bannerPivot);

  const banner = mesh(
    geo(`booth-banner-${width}`, () => new THREE.PlaneGeometry(width * 0.95, 0.50)),
    mat(0xffffff, { flat: true, map: comingSoonBanner(), side: THREE.DoubleSide }),
    { cast: false, receive: false },
  );
  banner.rotation.z = 0.14;
  banner.name = 'coming-soon-banner';
  bannerPivot.add(banner);

  /**
   * Shutter or open the booth. Shuttering desaturates the paintwork and hangs
   * a COMING SOON banner across the opening.
   * @param {boolean} on
   */
  const setComingSoon = (on) => {
    for (const e of mutable) e.mesh.material = on ? e.muted : e.vivid;
    bannerPivot.visible = on;
    g.userData.comingSoon = on;
  };
  setComingSoon(!!opts.comingSoon);

  // `hub.markImplemented()` only knows how to call setText on the name board.
  // Wrapping it here means a stall opening for business repaints its own booth
  // without hub.js needing to learn anything new.
  const baseSetText = label.userData.setText;
  label.userData.setText = (t) => {
    baseSetText(t);
    const flat = Array.isArray(t) ? t.join(' ') : String(t);
    setComingSoon(/coming\s*soon/i.test(flat));
  };

  g.userData.dimensions = { width, depth, height };
  g.userData.setComingSoon = setComingSoon;
  g.userData.family = fam.key;
  return g;
}

/**
 * A support post — barber-striped, as every carnival upright is.
 * Returned as a Mesh (not a Group) because callers reach for `.material`.
 *
 * @param {number} [height]
 * @param {number} [radius]
 * @returns {THREE.Mesh} with `userData.collider`.
 */
export function createPost(height = 1.2, radius = 0.05) {
  const m = mesh(
    geo(`post-${radius}-${height}`, () => new THREE.CylinderGeometry(
      radius, radius * 1.06, height, 12,
    )),
    mat(0xffffff, {
      rough: 0.6,
      map: barberStripe(ART.red, ART.cream, 6, [1, Math.max(1, Math.round(height / (radius * 6.3)))]),
    }),
  );
  m.position.y = height / 2;
  m.name = 'post';

  const cap = mesh(
    geo(`post-cap-${radius}`, () => new THREE.SphereGeometry(radius * 1.25, 10, 8)),
    mat(ART.gold, { rough: 0.45, metal: 0.35 }),
  );
  cap.position.y = height / 2;
  m.add(cap);

  const base = mesh(
    geo(`post-base-${radius}`, () => new THREE.CylinderGeometry(
      radius * 1.5, radius * 1.7, radius * 0.9, 12,
    )),
    mat(ART.iron, { rough: 0.6, metal: 0.4 }),
  );
  base.position.y = -height / 2 + radius * 0.45;
  m.add(base);

  m.userData.collider = { shape: 'cylinder', radius, halfHeight: height / 2, type: 'fixed' };
  return m;
}

/**
 * A shelf or table surface.
 * @param {number} [width] @param {number} [depth] @param {number} [height]
 * @returns {THREE.Group} with `userData.surfaceY` and `userData.collider`.
 */
export function createShelf(width = 2, depth = 0.45, height = 1.05) {
  const g = new THREE.Group();
  g.name = 'shelf';

  const top = mesh(
    slab(width, 0.07, depth, 0.014),
    mat(ART.wood, { rough: 0.78, map: woodGrain(ART.wood, [Math.max(1, Math.round(width / 0.7)), 1]) }),
  );
  top.position.y = height;
  g.add(top);

  // A painted front edge, so a plank reads as fairground furniture rather than
  // a floating board.
  const lip = mesh(
    slab(width, 0.05, 0.035, 0.008),
    mat(ART.red, { rough: 0.7 }),
  );
  lip.position.set(0, height - 0.045, -depth / 2 + 0.015);
  g.add(lip);

  for (const s of [-1, 1]) {
    const leg = mesh(
      slab(0.085, height, 0.085, 0.015),
      mat(ART.woodDark, { rough: 0.88, map: woodGrain(ART.woodDark) }),
    );
    leg.position.set((width / 2 - 0.1) * s, height / 2, 0);
    g.add(leg);

    const brace = mesh(
      slab(0.06, 0.06, depth * 0.8, 0.01),
      mat(ART.woodDark, { rough: 0.88 }),
    );
    brace.position.set((width / 2 - 0.1) * s, height * 0.32, 0);
    g.add(brace);
  }

  g.userData.surfaceY = height + 0.035;
  g.userData.collider = {
    shape: 'box', halfExtents: { x: width / 2, y: 0.035, z: depth / 2 }, type: 'fixed',
  };
  return g;
}
